if (typeof MatchingProducts === 'undefined') {
	/**
	 * MatchingProducts - Custom element for handling related product variants
	 * 
	 * This component manages the selection of size variants across matching products.
	 * When a user selects a size on one product and navigates to a related color variant,
	 * this component ensures the same size is selected on the new product.
	 */
	class MatchingProducts extends HTMLElement {
		constructor() {
			super();
			this.variantSelectedSize = null;
			this.matchingProductsData = null;
			this.hasAttemptedSelection = false;
			this.init();
		}

		init() {
			// Load matching products data from JSON
			this.loadMatchingProductsData();
			
			// Check if there's a saved matching product in session storage
			const savedMatchingProduct = sessionStorage.getItem('matchingProduct');

			if (savedMatchingProduct) {
				// User has already interacted with variants
				const matchingProductData = JSON.parse(savedMatchingProduct);
				this.variantSelectedSize = matchingProductData.variantSelectedSize || null;
				
				// Check if the current product is related to the saved one
				const currentProductHandle = window.location.pathname.match(/\/products\/([^\/]+)/)?.[1];
				const savedProductHandle = matchingProductData.productHandle;
				
				// Check if the products are in the same matching group
				this.isRelevantProduct = this.checkIfProductsAreRelated(currentProductHandle, savedProductHandle);
				
				if (this.isRelevantProduct && this.variantSelectedSize) {
					this.attemptSingleVariantSelection();
				}
			} else {
				// First time user interaction, save current product and variants
				this.saveCurrentSelection();
			}

			// Add event listeners for variant changes
			this.addEventListeners();
		}

		attemptSingleVariantSelection() {
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => {
					setTimeout(() => {
						this.selectCorrectVariants();
					}, 150);
				});
			} else {
				setTimeout(() => {
					this.selectCorrectVariants();
				}, 150);
			}
		}

		/**
		 * Load product data from a JSON script tag in the DOM
		 * This data contains information about matching products and their variants
		 */
		loadMatchingProductsData() {
			const dataScript = document.getElementById('matching-products-data');
			if (dataScript) {
				try {
					const data = JSON.parse(dataScript.textContent || '{}');
					this.matchingProductsData = data.matching_products;

					// Enhance the matching product items with additional data
					this.enhanceMatchingProductItems();
				} catch (e) {
					console.error('Error parsing matching products data:', e);
				}
			}
		}
		
		/**
		 * Enhance matching product items with additional data attributes
		 * - Adds product ID as data attribute
		 * - Marks unavailable products with a class
		 */
		enhanceMatchingProductItems() {
			if (!this.matchingProductsData) return;
			
			const matchingProductItems = document.querySelectorAll('.matching-products__item');
			matchingProductItems.forEach(item => {
				const href = item.getAttribute('href');
				if (!href) return;
				
				// Extract the handle from the href
				const handle = href.split('/').pop();
				
				// Find the matching product data
				const productData = this.matchingProductsData.find(p => p.handle === handle);
				if (productData) {
					// Store the product data as a data attribute
					item.setAttribute('data-product-id', productData.id);
					
					// Add a class if the product is unavailable
					const isAvailable = productData.variants.some(v => v.available);
					if (!isAvailable) {
						item.classList.add('matching-products__item--unavailable');
					}
				}
			});
		}

		/**
		 * Select the correct variant based on the saved size
		 * This is called when navigating between matching products
		 */
		selectCorrectVariants() {
			if (!this.variantSelectedSize) return;
			
			const sizeValue = this.variantSelectedSize;
			
			// Find all size inputs
			const sizeInputs = document.querySelectorAll('input[type="radio"]');
			let sizeInput = null;
			
			// First find the matching size input without checking it
			sizeInputs.forEach(input => {
				if (input.value === sizeValue && 
				    (input.name.toLowerCase().includes('size') || 
				     input.name.toLowerCase().includes('mărime'))) {
					sizeInput = input;
				}
			});
			
			// If we found a matching size input
			if (sizeInput && !sizeInput.disabled) {
				// WORKAROUND: Temporarily patch the pickup availability function if needed
				// This prevents errors when the pickup availability component isn't fully initialized
				const pickUpAvailability = document.querySelector('pickup-availability');
				const originalFetchAvailability = pickUpAvailability?.fetchAvailability;
				
				// If the function doesn't exist, create a temporary no-op function
				if (pickUpAvailability && !pickUpAvailability.fetchAvailability) {
					pickUpAvailability.fetchAvailability = function(variantId) {
						// This is a no-op function that will be replaced later
					};
				}
				
				try {
					// Simply check it and let Shopify's native handlers take over
					sizeInput.checked = true;
					
					// Use a simple change event - the native Shopify code expects this
					sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
				} catch (e) {
					console.error('Error selecting variant:', e);
				} finally {
					// Restore original function if we patched it
					if (pickUpAvailability && !originalFetchAvailability) {
						// Remove our temporary function after a delay to allow other processes to complete
						setTimeout(() => {
							delete pickUpAvailability.fetchAvailability;
						}, 500);
					}
				}
			}
		}

		/**
		 * Save the current product and selected size to session storage
		 */
		saveCurrentSelection() {
			const currentProduct = this.getCurrentProductInfo();
			if (currentProduct) {
				sessionStorage.setItem('matchingProduct', JSON.stringify(currentProduct));
			}
		}

		/**
		 * Get information about the current product
		 * @returns {Object} Object containing product handle, selected size, and product data
		 */
		getCurrentProductInfo() {
			// Get current product handle from URL
			const productHandle = window.location.pathname.match(/\/products\/([^\/]+)/)?.[1];
			if (!productHandle) return null;

			// Find the current product in our matching products data
			let currentProductData = null;
			if (this.matchingProductsData) {
				currentProductData = this.matchingProductsData.find(p => p.handle === productHandle);
			}

			return {
				productHandle,
				variantSelectedSize: this.variantSelectedSize,
				productData: currentProductData
			};
		}

		/**
		 * Handle variant change events
		 * @param {Event} event - The change event from the variant radio button
		 */
		handleVariantChange(event) {
			// Extract information from the change event
			const target = event.target;
			if (!target || target.tagName !== 'INPUT' || target.type !== 'radio') return;
			
			const name = target.name;
			const value = target.value;
			
			// Only store size variants
			if (name.toLowerCase().includes('size') || name.toLowerCase().includes('mărime')) {
				// Store just the value in variantSelectedSize
				this.variantSelectedSize = value;
				
				// Save to session storage
				this.saveCurrentSelection();
			}
		}

		/**
		 * Add event listeners for variant changes and product navigation
		 */
		addEventListeners() {
			// The key is to listen to the variant-radios component directly
			// This is how the theme handles variant changes
			const variantRadios = document.querySelector('variant-radios');
			if (variantRadios) {
				// Listen for the change event on the variant-radios component
				variantRadios.addEventListener('change', (event) => {
					this.handleVariantChange(event);
				});
				
				// Also capture initial selections - but only if we don't have a session storage value
				// This prevents the initial selection from overriding our session storage value
				if (!this.variantSelectedSize) {
					this.captureInitialSelections(variantRadios);
				}
			}

			// Listen for clicks on matching product items
			const matchingProductItems = document.querySelectorAll('.matching-products__item');
			matchingProductItems.forEach((item) => {
				item.addEventListener('click', (e) => {
					// Save the current selections before navigating to the new product
					this.saveCurrentSelection();
				});
			});

			// Also listen for DOMContentLoaded to ensure we capture initial state
			// But only if we don't have a session storage value
			if (!this.variantSelectedSize) {
				document.addEventListener('DOMContentLoaded', () => {
					this.captureInitialSelections(document.querySelector('variant-radios'));
					this.saveCurrentSelection();
				});
			}
		}
		
		/**
		 * Capture the initial size selection when the page loads
		 * @param {Element} variantRadios - The variant-radios component
		 */
		captureInitialSelections(variantRadios) {
			if (!variantRadios) return;
			
			// Find size inputs
			const sizeInputs = Array.from(variantRadios.querySelectorAll('input[type="radio"]')).filter(input => {
				const name = input.name;
				return name.toLowerCase().includes('size') || name.toLowerCase().includes('mărime');
			});
			
			// Find the checked size input
			const checkedSizeInput = sizeInputs.find(input => input.checked);
			
			if (checkedSizeInput) {
				const value = checkedSizeInput.value;
				
				// Store just the size value
				this.variantSelectedSize = value;
			}
		}

		/**
		 * Check if two products are related (part of the same matching group)
		 * @param {string} currentHandle - The handle of the current product
		 * @param {string} savedHandle - The handle of the saved product
		 * @returns {boolean} True if the products are related
		 */
		checkIfProductsAreRelated(currentHandle, savedHandle) {
			// If we don't have matching products data yet, we can't determine if they're related
			if (!this.matchingProductsData || !currentHandle || !savedHandle) {
				return false;
			}
			
			// If it's the same product, they're definitely related
			if (currentHandle === savedHandle) {
				return true;
			}
			
			// Check if both products are in our matching products data
			const currentProductInGroup = this.matchingProductsData.some(p => p.handle === currentHandle);
			const savedProductInGroup = this.matchingProductsData.some(p => p.handle === savedHandle);
			
			// If both products are in the matching group, they're related
			return currentProductInGroup && savedProductInGroup;
		}
	}

	// Register the custom element if it's not already defined
	if (!customElements.get('matching-products')) {
		customElements.define('matching-products', MatchingProducts);
	}
}
